import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import Navbar from '../components/Navbar';
import { PageLoader } from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { 
  Package, Plus, ArrowLeft, Crown, Truck, Tag, Edit2, 
  Trash2, X, Check, DollarSign, TrendingUp, Printer,
  CheckCircle, Clock, AlertCircle, ChevronDown, Search
} from 'lucide-react';

const CARRIERS = ['USPS', 'UPS', 'FedEx', 'DHL'];

export const SellerHubPage = () => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [listings, setListings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Bulk price modal
  const [showBulkPriceModal, setShowBulkPriceModal] = useState(false);
  const [bulkPriceType, setBulkPriceType] = useState('set'); // set, adjust, percent
  const [bulkPriceValue, setBulkPriceValue] = useState('');
  
  // Tracking modal
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('USPS');
  
  // Shipping label modal
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelData, setLabelData] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadData();
  }, [isAuthenticated, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, listingsRes] = await Promise.all([
        api().get('/seller-hub/analytics'),
        api().get(`/seller-hub/listings?status=${statusFilter}`)
      ]);
      setAnalytics(analyticsRes.data.summary);
      setListings(listingsRes.data.listings || []);
      setSelectedIds([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load seller hub data');
    } finally {
      setLoading(false);
    }
  };

  // Selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(listings.filter(l => l.status === 'active').map(l => l.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectListing = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Bulk price update
  const handleBulkPriceUpdate = async () => {
    if (!bulkPriceValue || selectedIds.length === 0) {
      toast.error('Please select listings and enter a price value');
      return;
    }

    try {
      const payload = { listing_ids: selectedIds };
      
      if (bulkPriceType === 'set') {
        payload.new_price = parseFloat(bulkPriceValue);
      } else if (bulkPriceType === 'adjust') {
        payload.price_adjustment = parseFloat(bulkPriceValue);
      } else if (bulkPriceType === 'percent') {
        payload.percentage_adjustment = parseFloat(bulkPriceValue);
      }

      const response = await api().post('/seller-hub/listings/bulk-price', payload);
      toast.success(`Updated prices for ${response.data.count} listings`);
      setShowBulkPriceModal(false);
      setBulkPriceValue('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update prices');
    }
  };

  // Bulk end listings
  const handleBulkEnd = async () => {
    if (selectedIds.length === 0) return;
    
    if (!window.confirm(`End ${selectedIds.length} listings? This cannot be undone.`)) return;

    try {
      const response = await api().post('/seller-hub/listings/bulk-end', {
        listing_ids: selectedIds,
        reason: 'ended_by_seller'
      });
      toast.success(`Ended ${response.data.ended_count} listings`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end listings');
    }
  };

  // Add tracking number
  const handleAddTracking = async () => {
    if (!trackingNumber.trim() || !selectedOrder) {
      toast.error('Please enter a tracking number');
      return;
    }

    try {
      await api().post('/seller-hub/orders/tracking', {
        order_id: selectedOrder.order.id,
        tracking_number: trackingNumber.trim(),
        carrier: carrier,
        notify_buyer: true
      });
      toast.success('Tracking added and buyer notified!');
      setShowTrackingModal(false);
      setTrackingNumber('');
      setSelectedOrder(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add tracking');
    }
  };

  // Print shipping label
  const handlePrintLabel = async (orderId) => {
    try {
      const response = await api().get(`/seller-hub/shipping-label/${orderId}`);
      setLabelData(response.data);
      setShowLabelModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate label');
    }
  };

  // Filter listings by search
  const filteredListings = listings.filter(listing => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      listing.player_name?.toLowerCase().includes(query) ||
      listing.set_name?.toLowerCase().includes(query) ||
      listing.sport?.toLowerCase().includes(query)
    );
  });

  if (!isAuthenticated) return null;
  if (loading) return <PageLoader />;

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="seller-hub-page">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Back button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-4xl uppercase text-white">Seller Hub</h1>
            <p className="text-zinc-400 mt-1">Manage your listings and orders</p>
          </div>
          
          {user?.subscription_active ? (
            <Link to="/sell">
              <Button className="btn-primary" data-testid="new-listing-btn">
                <Plus className="w-5 h-5 mr-2" />
                New Listing
              </Button>
            </Link>
          ) : (
            <Link to="/subscription">
              <Button className="btn-primary">
                <Crown className="w-5 h-5 mr-2" />
                Become a Seller
              </Button>
            </Link>
          )}
        </div>

        {/* Analytics Summary Widget */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="analytics-widget">
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Total Sales (30d)</p>
                  <p className="text-xl font-bold text-white">${analytics.total_revenue_30d?.toLocaleString()}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/10 rounded-lg">
                  <Truck className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Orders to Ship</p>
                  <p className="text-xl font-bold text-white">{analytics.orders_to_ship}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Tag className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Active Listings</p>
                  <p className="text-xl font-bold text-white">{analytics.active_listings}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Orders (30d)</p>
                  <p className="text-xl font-bold text-white">{analytics.orders_30d}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-zinc-700 pb-4">
          {[
            { value: 'active', label: 'Active', icon: CheckCircle, color: 'emerald' },
            { value: 'sold', label: 'Sold', icon: DollarSign, color: 'blue' },
            { value: 'unsold', label: 'Unsold/Ended', icon: AlertCircle, color: 'zinc' }
          ].map(tab => (
            <Button
              key={tab.value}
              variant={statusFilter === tab.value ? 'default' : 'ghost'}
              onClick={() => setStatusFilter(tab.value)}
              className={statusFilter === tab.value ? `bg-${tab.color}-600` : 'text-zinc-400'}
              data-testid={`tab-${tab.value}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Search and Bulk Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search listings..."
              className="pl-10 bg-zinc-800 border-zinc-700"
              data-testid="search-input"
            />
          </div>
          
          {statusFilter === 'active' && selectedIds.length > 0 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowBulkPriceModal(true)}
                className="border-zinc-600 text-zinc-300"
                data-testid="bulk-price-btn"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Update Price ({selectedIds.length})
              </Button>
              <Button
                variant="outline"
                onClick={handleBulkEnd}
                className="border-red-600/50 text-red-400 hover:bg-red-500/10"
                data-testid="bulk-end-btn"
              >
                <X className="w-4 h-4 mr-2" />
                End ({selectedIds.length})
              </Button>
            </div>
          )}
        </div>

        {/* Listings Grid */}
        {filteredListings.length === 0 ? (
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-12 text-center">
            <Package className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-white mb-2">
              {statusFilter === 'active' ? 'No active listings' : 
               statusFilter === 'sold' ? 'No sold items' : 'No ended listings'}
            </h2>
            <p className="text-zinc-400 mb-6">
              {statusFilter === 'active' ? 'Start selling your cards.' : 
               'Items will appear here when available.'}
            </p>
            {statusFilter === 'active' && user?.subscription_active && (
              <Link to="/sell">
                <Button className="btn-primary">
                  <Plus className="w-5 h-5 mr-2" />
                  Create Listing
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Select All Header (for active listings) */}
            {statusFilter === 'active' && filteredListings.length > 0 && (
              <div className="flex items-center gap-3 px-4 py-2 bg-zinc-800/50 rounded-lg">
                <Checkbox
                  checked={selectAll}
                  onCheckedChange={handleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <span className="text-sm text-zinc-400">
                  {selectAll ? `All ${filteredListings.length} selected` : 'Select all'}
                </span>
              </div>
            )}

            {/* Listings */}
            {filteredListings.map((listing) => (
              <div 
                key={listing.id} 
                className="bg-zinc-900/50 border border-white/5 rounded-xl p-4 hover:border-white/10 transition-all"
                data-testid={`listing-row-${listing.id}`}
              >
                <div className="flex items-center gap-4">
                  {/* Checkbox for active listings */}
                  {statusFilter === 'active' && (
                    <Checkbox
                      checked={selectedIds.includes(listing.id)}
                      onCheckedChange={() => handleSelectListing(listing.id)}
                      data-testid={`checkbox-${listing.id}`}
                    />
                  )}
                  
                  {/* Image */}
                  <div className="w-16 h-20 rounded-lg overflow-hidden bg-zinc-800 shrink-0">
                    <img
                      src={listing.images?.[0] || listing.image_url || '/placeholder-card.png'}
                      alt={listing.player_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/listing/${listing.id}`} className="hover:text-blue-400">
                      <h3 className="font-semibold text-white truncate">
                        {listing.player_name}
                      </h3>
                    </Link>
                    <p className="text-sm text-zinc-500 truncate">
                      {listing.year} {listing.set_name} {listing.parallel && `- ${listing.parallel}`}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-emerald-400 font-bold">${listing.price?.toLocaleString()}</span>
                      {listing.grading_company && listing.grade && (
                        <span className="text-xs bg-zinc-700 px-2 py-0.5 rounded">
                          {listing.grading_company} {listing.grade}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        listing.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                        listing.status === 'sold' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-zinc-700 text-zinc-400'
                      }`}>
                        {listing.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {listing.status === 'active' && (
                      <>
                        <Link to={`/edit-listing/${listing.id}`}>
                          <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </Link>
                      </>
                    )}
                    
                    {listing.status === 'sold' && listing.order && (
                      <div className="flex items-center gap-2">
                        {listing.order.tracking_number ? (
                          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Shipped
                          </span>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedOrder(listing);
                                setShowTrackingModal(true);
                              }}
                              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                              data-testid={`add-tracking-${listing.id}`}
                            >
                              <Truck className="w-4 h-4 mr-1" />
                              Add Tracking
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePrintLabel(listing.order.id)}
                              className="border-zinc-600 text-zinc-300"
                              data-testid={`print-label-${listing.id}`}
                            >
                              <Printer className="w-4 h-4 mr-1" />
                              Print Label
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Sold Item Buyer Info */}
                {listing.status === 'sold' && listing.buyer && (
                  <div className="mt-4 pt-4 border-t border-zinc-800">
                    <p className="text-sm text-zinc-400">
                      <span className="text-zinc-500">Buyer:</span> {listing.buyer.name}
                    </p>
                    {listing.buyer.shipping_address && (
                      <p className="text-xs text-zinc-500 mt-1">
                        {listing.buyer.shipping_address.city}, {listing.buyer.shipping_address.state}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Bulk Price Update Modal */}
      <Dialog open={showBulkPriceModal} onOpenChange={setShowBulkPriceModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Update Prices ({selectedIds.length} items)</DialogTitle>
            <DialogDescription>
              Choose how to update prices for selected listings.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              {[
                { value: 'set', label: 'Set Price' },
                { value: 'adjust', label: 'Adjust (+/-)' },
                { value: 'percent', label: 'Percentage' }
              ].map(opt => (
                <Button
                  key={opt.value}
                  variant={bulkPriceType === opt.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setBulkPriceType(opt.value)}
                  className={bulkPriceType === opt.value ? 'bg-blue-600' : 'border-zinc-600'}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">
                {bulkPriceType === 'set' ? 'New Price ($)' :
                 bulkPriceType === 'adjust' ? 'Amount to Add/Subtract ($)' :
                 'Percentage Change (%)'}
              </label>
              <Input
                type="number"
                value={bulkPriceValue}
                onChange={(e) => setBulkPriceValue(e.target.value)}
                placeholder={bulkPriceType === 'percent' ? 'e.g., -10 for 10% off' : 'Enter amount'}
                className="bg-zinc-800 border-zinc-700"
                data-testid="bulk-price-input"
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowBulkPriceModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleBulkPriceUpdate} className="bg-blue-600">
                Update Prices
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Tracking Modal */}
      <Dialog open={showTrackingModal} onOpenChange={setShowTrackingModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Add Tracking Number</DialogTitle>
            <DialogDescription>
              {selectedOrder && `For: ${selectedOrder.player_name}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Carrier</label>
              <Select value={carrier} onValueChange={setCarrier}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {CARRIERS.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Tracking Number</label>
              <Input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Enter tracking number"
                className="bg-zinc-800 border-zinc-700"
                data-testid="tracking-input"
              />
            </div>
            
            <p className="text-xs text-zinc-500">
              The buyer will be automatically notified via email.
            </p>
            
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowTrackingModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddTracking} className="bg-emerald-600">
                <Truck className="w-4 h-4 mr-2" />
                Add & Notify Buyer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shipping Label Modal */}
      <Dialog open={showLabelModal} onOpenChange={setShowLabelModal}>
        <DialogContent className="bg-white text-black max-w-md">
          <DialogHeader>
            <DialogTitle className="text-black">Shipping Label (4x6)</DialogTitle>
          </DialogHeader>
          
          {labelData && (
            <div className="border-2 border-black p-6 font-mono text-sm" id="shipping-label">
              {/* From Address */}
              <div className="mb-6">
                <p className="text-xs text-gray-500 mb-1">FROM:</p>
                <p className="font-bold">{labelData.from_address.name}</p>
                <p>{labelData.from_address.street1}</p>
                {labelData.from_address.street2 && <p>{labelData.from_address.street2}</p>}
                <p>{labelData.from_address.city}, {labelData.from_address.state} {labelData.from_address.zip}</p>
              </div>
              
              {/* To Address */}
              <div className="border-2 border-black p-4 bg-gray-50">
                <p className="text-xs text-gray-500 mb-1">TO:</p>
                <p className="font-bold text-lg">{labelData.to_address.name}</p>
                <p className="text-lg">{labelData.to_address.street1}</p>
                {labelData.to_address.street2 && <p className="text-lg">{labelData.to_address.street2}</p>}
                <p className="text-lg font-bold">
                  {labelData.to_address.city}, {labelData.to_address.state} {labelData.to_address.zip}
                </p>
              </div>
              
              {/* Item & Tracking */}
              <div className="mt-4 pt-4 border-t border-gray-300">
                <p className="text-xs text-gray-500">Item: {labelData.item_description}</p>
                {labelData.tracking_number && (
                  <p className="text-xs text-gray-500 mt-1">
                    Tracking: {labelData.carrier} {labelData.tracking_number}
                  </p>
                )}
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setShowLabelModal(false)} className="text-black">
              Close
            </Button>
            <Button 
              onClick={() => window.print()} 
              className="bg-black text-white"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Label
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print styles for label */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #shipping-label, #shipping-label * {
            visibility: visible;
          }
          #shipping-label {
            position: absolute;
            left: 0;
            top: 0;
            width: 4in;
            height: 6in;
          }
        }
      `}</style>
    </div>
  );
};

export default SellerHubPage;
